import { CloseOutlined, ContainerOutlined } from '@ant-design/icons';
import { Form, Input, InputNumber, Switch, Tooltip, Typography } from 'antd';
import type { CSSProperties } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { FormSection } from '@/api/workflows';

const { Text } = Typography;

const RAIL_WIDTH = 320;
const RAIL_BASE: CSSProperties = {
  flexShrink: 0,
  width: RAIL_WIDTH,
  borderLeft: '1px solid #e5e7eb',
  background: '#fff',
  padding: 18,
  overflowY: 'auto',
};
const COMPACT_ITEM: CSSProperties = { marginBottom: 10 };

// Defaults applied the first time an originator flips Repeatable on. Kept in
// sync with the backend's coherence rules (min >= 1, max >= min).
const DEFAULT_MIN_INSTANCES = 1;
const DEFAULT_MAX_INSTANCES = 3;

type SaveDetailsPayload = { titleEn: string; titleFr: string | null };
type SaveRepeatablePayload = {
  repeatable: boolean;
  minInstances: number;
  maxInstances: number | null;
};

type Props = {
  /** The section being edited; null hides the panel (nothing selected). */
  section: FormSection | null;
  /** Persist the bilingual section name (PATCH /sections/{id}). */
  onSaveDetails: (payload: SaveDetailsPayload) => Promise<void>;
  /** Persist the repeatable toggle + min/max bounds (PUT /sections/{id}/repeatable). */
  onSaveRepeatable: (payload: SaveRepeatablePayload) => Promise<void>;
  onClose: () => void;
};

type FormValues = {
  titleEn: string;
  titleFr: string;
  repeatable: boolean;
  minInstances: number;
  maxInstances: number | null;
};

/**
 * Right-rail editor for a selected form section — the section-level sibling of
 * {@link FieldPropertiesPanel}. Deliberately minimal for now: bilingual name +
 * a Repeatable toggle that reveals min/max instance bounds. Auto-saves on every
 * change (debounced) with the same three-state save-dot the field panel uses.
 *
 * Only rendered for the workflow-backed editor (the Custom Editor has no
 * repeatable authoring); the shell passes handlers that route through
 * `dataSource.editSectionDetails` / `dataSource.setSectionRepeatable`.
 */
export function SectionPropertiesPanel({
  section,
  onSaveDetails,
  onSaveRepeatable,
  onClose,
}: Props) {
  // Mounted only when a section is selected; caller owns dismissal via onClose.
  if (!section) return null;
  // Key on section.id so switching sections gets a fresh form instance (no
  // stale-values race when AntD reuses the form between mounts) — same guard
  // FieldPropertiesPanel uses for fields.
  return (
    <SectionEditor
      key={section.id}
      section={section}
      onSaveDetails={onSaveDetails}
      onSaveRepeatable={onSaveRepeatable}
      onClose={onClose}
    />
  );
}

function SectionEditor({
  section,
  onSaveDetails,
  onSaveRepeatable,
  onClose,
}: {
  section: FormSection;
  onSaveDetails: Props['onSaveDetails'];
  onSaveRepeatable: Props['onSaveRepeatable'];
  onClose: Props['onClose'];
}) {
  const { t } = useTranslation();
  const [form] = Form.useForm<FormValues>();
  // Reactive watches so the min/max inputs reveal in lockstep with the toggle
  // and the max floor tracks the current min.
  const repeatable = Form.useWatch<boolean | undefined>('repeatable', form);
  const minInstances = Form.useWatch<number | undefined>('minInstances', form);

  // Captured once at mount (keyed on section.id so switching sections remounts).
  // For an already-repeatable section reflect its true stored bounds (a null
  // max stays empty = unlimited so we never silently cap it on the next edit).
  // For a not-yet-repeatable section seed the "turn it on" defaults (1 / 3) so
  // flipping Repeatable reveals them rather than empty inputs.
  const [initialValues] = useState<FormValues>(() => {
    const isRepeatable = section.repeatable ?? false;
    return {
      titleEn: section.titleEn,
      titleFr: section.titleFr,
      repeatable: isRepeatable,
      minInstances: isRepeatable
        ? (section.minInstances ?? DEFAULT_MIN_INSTANCES)
        : DEFAULT_MIN_INSTANCES,
      maxInstances: isRepeatable ? (section.maxInstances ?? null) : DEFAULT_MAX_INSTANCES,
    };
  });

  type AutoSaveStatus = 'saved' | 'saving' | 'error';
  const [autoSaveStatus, setAutoSaveStatus] = useState<AutoSaveStatus>('saved');
  const [autoSaveErrorText, setAutoSaveErrorText] = useState<string | null>(null);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Counts in-flight requests so a stale response can't mask a newer failure.
  const inFlightCountRef = useRef(0);

  const persistNow = useCallback(async () => {
    inFlightCountRef.current += 1;
    setAutoSaveStatus('saving');
    try {
      const values = await form.validateFields();
      const isRepeatable = values.repeatable ?? false;
      await onSaveDetails({
        titleEn: values.titleEn,
        titleFr: values.titleFr ? values.titleFr : null,
      });
      // Collapse the bounds to their coherent shape when the section is not
      // repeatable — the backend refuses a non-null MaxInstances without
      // Repeatable = true, so never send stale bounds from the hidden inputs.
      await onSaveRepeatable({
        repeatable: isRepeatable,
        minInstances: isRepeatable ? (values.minInstances ?? DEFAULT_MIN_INSTANCES) : 1,
        maxInstances: isRepeatable ? (values.maxInstances ?? null) : null,
      });
      inFlightCountRef.current -= 1;
      if (inFlightCountRef.current === 0) {
        setAutoSaveStatus('saved');
        setAutoSaveErrorText(null);
      }
    } catch (err) {
      inFlightCountRef.current -= 1;
      const msg = err instanceof Error ? err.message : String(err);
      setAutoSaveErrorText(msg);
      setAutoSaveStatus('error');
    }
  }, [form, onSaveDetails, onSaveRepeatable]);

  // Debounce 500ms so rapid typing produces one round-trip, not N. Matches the
  // field panel's cadence.
  const handleValuesChange = () => {
    setAutoSaveStatus('saving');
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(() => {
      debounceTimerRef.current = null;
      void persistNow();
    }, 500);
  };

  // Flush a pending save if the panel closes mid-debounce, and clear the timer
  // on unmount so a stale callback can't fire after the panel is gone.
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
        void persistNow();
      }
    };
  }, [persistNow]);

  const statusLabel =
    autoSaveStatus === 'saved'
      ? t('workflow.builder.properties.autoSaveSaved')
      : autoSaveStatus === 'saving'
        ? t('workflow.builder.properties.autoSaveSaving')
        : t('workflow.builder.properties.autoSaveError', { error: autoSaveErrorText ?? '' });

  return (
    <aside style={RAIL_BASE}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          paddingBottom: 14,
          marginBottom: 16,
          borderBottom: '1px solid #f1f5f9',
        }}
      >
        <span
          style={{
            width: 32,
            height: 32,
            borderRadius: 8,
            background: '#eef2ff',
            color: '#4f46e5',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 16,
          }}
        >
          <ContainerOutlined />
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <Text strong style={{ fontSize: 14, lineHeight: 1.2, display: 'block' }}>
            {t('workflow.builder.sectionProperties.title')}
          </Text>
          <Text type="secondary" style={{ fontSize: 12, lineHeight: 1.2 }}>
            {t('workflow.builder.sectionProperties.subtitle')}
          </Text>
        </div>
        <Tooltip title={statusLabel}>
          <span
            role="status"
            aria-label={statusLabel}
            style={{
              display: 'inline-block',
              width: 10,
              height: 10,
              borderRadius: 999,
              background:
                autoSaveStatus === 'saved'
                  ? '#16a34a'
                  : autoSaveStatus === 'saving'
                    ? '#f59e0b'
                    : '#dc2626',
              transition: 'background-color 140ms ease',
              flexShrink: 0,
            }}
          />
        </Tooltip>
        <Tooltip title={t('workflow.builder.properties.close')}>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('workflow.builder.properties.close')}
            style={{
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              color: '#6b7280',
              display: 'inline-flex',
              alignItems: 'center',
              padding: 4,
            }}
          >
            <CloseOutlined />
          </button>
        </Tooltip>
      </div>

      <style>{`
        .builder-section-props-form .ant-form-item-label { padding-bottom: 2px; }
        .builder-section-props-form .ant-form-item-label > label { height: auto; }
      `}</style>
      <Form
        form={form}
        layout="vertical"
        requiredMark={false}
        initialValues={initialValues}
        onValuesChange={handleValuesChange}
        className="builder-section-props-form"
      >
        <Form.Item
          label={t('workflow.builder.sectionProperties.nameEn')}
          name="titleEn"
          style={COMPACT_ITEM}
        >
          <Input />
        </Form.Item>
        <Form.Item
          label={t('workflow.builder.sectionProperties.nameFr')}
          name="titleFr"
          style={COMPACT_ITEM}
        >
          <Input />
        </Form.Item>

        <div
          style={{
            marginTop: 8,
            paddingTop: 14,
            borderTop: '1px dashed #e5e7eb',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
            <Form.Item name="repeatable" valuePropName="checked" noStyle>
              <Switch />
            </Form.Item>
            <Text strong style={{ fontSize: 13 }}>
              {t('workflow.builder.sectionProperties.repeatable')}
            </Text>
          </div>
          <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 12 }}>
            {t('workflow.builder.sectionProperties.repeatableHint')}
          </Text>

          {repeatable && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <Form.Item
                label={t('workflow.builder.sectionProperties.minInstances')}
                name="minInstances"
                style={COMPACT_ITEM}
                rules={[
                  {
                    required: true,
                    message: t('workflow.builder.sectionProperties.minRequired'),
                  },
                ]}
              >
                {/* 0 = optional block: nothing renders until the advisor
                    clicks Add. Pair with a max of 1 for the "add this once,
                    or not at all" pattern. */}
                <InputNumber min={0} max={100} style={{ width: '100%' }} controls={false} />
              </Form.Item>
              <Form.Item
                label={t('workflow.builder.sectionProperties.maxInstances')}
                name="maxInstances"
                style={COMPACT_ITEM}
                dependencies={['minInstances']}
                rules={[
                  {
                    validator: (_rule, value: number | null | undefined) => {
                      if (value === null || value === undefined) return Promise.resolve();
                      const min = minInstances ?? DEFAULT_MIN_INSTANCES;
                      if (value < min) {
                        return Promise.reject(
                          new Error(t('workflow.builder.sectionProperties.maxBelowMin')),
                        );
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <InputNumber
                  // A cap of 0 would leave a section that can never hold an
                  // instance, so the cap floor is 1 even when the min is 0.
                  min={Math.max(1, minInstances ?? 1)}
                  max={100}
                  style={{ width: '100%' }}
                  controls={false}
                />
              </Form.Item>
            </div>
          )}
        </div>
      </Form>
    </aside>
  );
}
